import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useAccountMargin } from '../../../hooks/useAccountMargin';
import { usePositions } from '../../../hooks/usePositions';
import { ProductType } from '../../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { formatDollar, formatNumber } from '../../../lib/formatters';
import { Gwei } from '../../../lib/gwei';
import { getDecimalPlaces } from '../../../lib/math';
import { Button } from '../../catalyst-ui-kit/button';
import { useTradeFormAmountError } from '../hooks/useTradeFormAmountError';
import { TradeFormAmountInput } from './TradeFormAmountInput';
import { TradeFormLimitPriceInput } from './TradeFormLimitPriceInput';
import { useTradeFormStore } from './TradeFormState';
import { StatRow, StatRowValue } from './TradeFormStats';
import { TradeFormTotalInput } from './TradeFormTotalInput';

// const labelId = 'trade-form-amount-input';
const errorId = 'trade-form-amount-error';

type TradeFormInputsUiProps = {
  product: ProductType;
  activeInput: 'amount' | 'total';
  currentPosition: bigint | undefined;
  availableMargin: bigint | undefined;
  amountErrorMessage: string | undefined;
  TradeFormLimitPriceInput: typeof TradeFormLimitPriceInput;
};

export const TradeFormInputsUi: React.FC<TradeFormInputsUiProps> = ({
  product,
  activeInput,
  availableMargin,
  currentPosition,
  amountErrorMessage,
  TradeFormLimitPriceInput,
}) => {
  const setAmount = useTradeFormStore((s) => s.setAmount);
  const setActiveInput = useTradeFormStore((s) => s.setActiveInput);
  const decimals = getDecimalPlaces(product.lotSize);

  return (
    <div role="group" aria-labelledby="amount-label" className="space-y-3">
      <div className="flex flex-col gap-2">
        <StatRow label="Available Balance">
          <StatRowValue value={availableMargin} formatValue={formatDollar} />
        </StatRow>
        <StatRow label="Current Position">
          <Button
            text
            size="2xs"
            color="tertiary"
            disabled={!currentPosition}
            onClick={() => {
              if (currentPosition) {
                setAmount(Gwei.abs(currentPosition));
                setActiveInput('amount');
              }
            }}
          >
            {currentPosition
              ? `${formatNumber(Gwei.abs(currentPosition), { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${product.baseTokenName}`
              : '-'}
          </Button>
        </StatRow>
      </div>
      <TradeFormLimitPriceInput product={product} />
      <div className="space-y-1">
        {activeInput === 'amount' ? (
          <TradeFormAmountInput product={product} amountErrorMessage={amountErrorMessage} />
        ) : (
          <TradeFormTotalInput product={product} amountErrorMessage={amountErrorMessage} />
        )}
        {amountErrorMessage && (
          <p id={errorId} role="alert" className="flex items-center gap-1 text-3xs text-contentRed">
            <InformationCircleIcon width={16} height={16} className="shrink-0" />
            {amountErrorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export const TradeFormInputs: React.FC<{ product: ProductType }> = ({ product }) => {
  const { errorMessage: amountErrorMessage } = useTradeFormAmountError(product);
  const activeInput = useTradeFormStore((x) => x.activeInput);

  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: marginData } = useAccountMargin();
  const availableMargin = marginData?.totalAvailable ?? 0n;

  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const currentPosition = positions?.find((position) => position.productId === product.id)?.size;

  return (
    <TradeFormInputsUi
      product={product}
      activeInput={activeInput}
      availableMargin={availableMargin}
      currentPosition={currentPosition}
      amountErrorMessage={amountErrorMessage}
      TradeFormLimitPriceInput={TradeFormLimitPriceInput}
    />
  );
};
